import React, { ReactNode, useRef, useState, useEffect, ReactElement } from 'react';
import ReactDOM from 'react-dom';

interface Props {
    children: ReactNode;
    content: ReactNode | string;
    delay?: number;
    isSmall?: boolean;
    hoverOutDelay?: number;
    showArrow?: boolean;
}

const HoverTooltip = ({ children, content, delay = 100, hoverOutDelay = 50, showArrow = true }: Props) => {
    const [active, setActive] = useState(false);
    const [coords, setCoords] = useState({ left: 0, top: 0 });
    const wrapperRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [isCustomContent, setIsCustomContent] = useState(typeof content !== 'string');

    const showTip = () => {
        if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
        const rect = wrapperRef.current?.getBoundingClientRect();
        if (!rect) return;

        timeoutRef.current = setTimeout(() => {
            setCoords({
                left: rect.x + rect.width / 2,
                top: rect.top + window.scrollY,
            });
            setActive(true);
        }, delay);
    };

    const hideTip = () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        hideTimeoutRef.current = setTimeout(() => {
            setActive(false);
        }, 0);
    };

    useEffect(() => {
        if (active && tooltipRef.current) {
            const tooltipHeight = tooltipRef.current.offsetHeight;
            setCoords((prev) => ({
                ...prev,
                top: prev.top - tooltipHeight - (showArrow ? 12 : 8), // adjust for arrow
            }));
            tooltipRef.current.classList.remove('opacity-0');
        }
    }, [active, showArrow]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
        };
    }, []);

    useEffect(() => {
        setIsCustomContent(typeof content !== 'string');
    }, [content]);

    return (
        <div ref={wrapperRef} onMouseEnter={showTip} onMouseLeave={hideTip} className="relative inline-block">
            {children}
            {active &&
                ReactDOM.createPortal(
                    <div
                        ref={tooltipRef}
                        className={`opacity-0 absolute ${
                            isCustomContent ? '' : 'px-3 py-2'
                        } rounded shadow-lg min-w-max bg-gray-800 text-white text-sm z-99999`}
                        style={{
                            left: coords.left,
                            top: coords.top,
                            transform: 'translateX(-50%)',
                            position: 'absolute',
                        }}
                    >
                        {content}
                        {showArrow && (
                            <div
                                className="absolute left-1/2 transform -translate-x-1/2"
                                style={{
                                    bottom: -6,
                                    width: 0,
                                    height: 0,
                                    borderLeft: '6px solid transparent',
                                    borderRight: '6px solid transparent',
                                    borderTop: `6px solid ${isCustomContent ? '' : '#1f2937'}`, // Tailwind gray-800
                                }}
                            />
                        )}
                    </div>,
                    document.body,
                )}
        </div>
    );
};

export default HoverTooltip;
